import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Paper, TextField, IconButton, Alert } from '@mui/material';
import { CVHttpService } from '../../services';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function PreviewStep({ cvId, onFinish }) {
    const [shareURL, setShareURL] = useState('');
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        loadCVData();
    }, [cvId]);

    const loadCVData = async () => {
        if (!cvId) return;
        try {
            const data = await CVHttpService.loadCV(cvId);
            if (data.share_uuid) {
                const url = CVHttpService.getShareableURL(data.share_uuid);
                setShareURL(url);
            }
        } catch (err) {
            console.error('Failed to load CV', err);
        }
    };

    const handleCopyURL = async () => {
        try {
            await navigator.clipboard.writeText(shareURL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            alert('Failed to copy URL');
        }
    };

    const handleDownloadPDF = async () => {
        setLoading(true);
        try {
            await CVHttpService.downloadPDF(cvId);
        } catch (err) {
            alert('Failed to download PDF');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
                Preview & Finish
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your CV is ready! Download it as PDF or share it with a shareable link.
            </Typography>

            <Stack spacing={3}>
                <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Shareable URL
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Share this link with employers or add it to your job applications.
                    </Typography>
                    {shareURL ? (
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                value={shareURL}
                                readOnly
                                size="small"
                                sx={{ bgcolor: 'grey.50' }}
                            />
                            <IconButton
                                onClick={handleCopyURL}
                                color={copied ? 'success' : 'default'}
                                title="Copy URL"
                            >
                                {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                            </IconButton>
                        </Stack>
                    ) : (
                        <Alert severity="info">Generating shareable URL...</Alert>
                    )}
                </Paper>

                <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Download PDF
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Download your CV as a PDF file.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadPDF}
                        disabled={loading}
                    >
                        {loading ? 'Downloading...' : 'Download PDF'}
                    </Button>
                </Paper>

                <Alert severity="success">
                    <Typography variant="body2">
                        Your CV has been created successfully! You can edit it anytime from your CV list.
                    </Typography>
                </Alert>
            </Stack>
        </Box>
    );
}

export default PreviewStep;

